import { useEffect } from 'react';
import {
  useShadowRoot,
  TechDocsAddonLocations,
} from '@backstage/plugin-techdocs-react';
import { attachTechDocsAddonComponentData } from '@backstage/plugin-techdocs-react/alpha';

const STYLE_ID = 'aunoa-techdocs-overrides';
const CHROME_STYLE_ID = 'aunoa-techdocs-chrome-overrides';

/**
 * CSS para el "chrome" (header, toolbar, contenedor) que está FUERA
 * del shadow root. Lo inyectamos en document.head desde el addon,
 * porque `MuiCssBaseline.styleOverrides` con template string no se
 * procesa correctamente por Backstage (queda como código muerto).
 *
 * Las reglas usan `body.aunoa-techdocs-active` como gate — la clase
 * la añadimos en mount, por lo que estos overrides solo afectan al
 * reader, no a las páginas de catalog/APIs/etc.
 */
const chromeCss = `
  /* Header (banner gradient + título + metadata): de 139px → ~76px */
  body.aunoa-techdocs-active header[class*="BackstageHeader-header"] {
    padding: 12px 24px 10px !important;
    min-height: unset !important;
  }
  body.aunoa-techdocs-active [class*="BackstageHeader-breadcrumb"] {
    margin-bottom: 4px !important;
  }
  body.aunoa-techdocs-active [class*="BackstageHeader-title"] {
    font-size: 1.3rem !important;
    line-height: 1.2 !important;
    margin: 0 !important;
  }
  body.aunoa-techdocs-active [class*="BackstageHeader-subtitle"] {
    font-size: 0.78rem !important;
    margin: 2px 0 0 !important;
    opacity: 0.75 !important;
  }
  /* Pills Component/Owner/Lifecycle/Source: más densas */
  body.aunoa-techdocs-active [class*="BackstageHeaderLabel-root"] {
    margin-right: 16px !important;
  }
  body.aunoa-techdocs-active [class*="BackstageHeaderLabel-label"] {
    font-size: 0.62rem !important;
    margin-bottom: 1px !important;
    letter-spacing: 0.04em !important;
    opacity: 0.7 !important;
  }
  body.aunoa-techdocs-active [class*="BackstageHeaderLabel-value"] {
    font-size: 0.78rem !important;
  }
  body.aunoa-techdocs-active [class*="BackstageHeader-rightItemsBox"] {
    gap: 4px !important;
  }
  /* El gradient banner sigue ahí pero más slim — no quitamos el color
     porque ayuda a contextualizar "estás en docs" */

  /* Gap entre header + content reducido */
  body.aunoa-techdocs-active article[class*="BackstageContent"] {
    padding-top: 6px !important;
  }
`;

const customCss = `
  /* ═══════════════════════════════════════════
     MkDocs TABS bar — the main navigation
     ═══════════════════════════════════════════ */
  .md-tabs {
    background: var(--md-primary-fg-color) !important;
    padding: 0 12px !important;
    font-size: 0.9rem !important;
  }
  .md-tabs__link {
    font-size: 0.9rem !important;
    padding: 10px 16px !important;
    opacity: 0.85 !important;
    font-weight: 500 !important;
    letter-spacing: 0.01em !important;
  }
  .md-tabs__link--active,
  .md-tabs__item--active .md-tabs__link {
    opacity: 1 !important;
    font-weight: 600 !important;
  }

  /* ═══════════════════════════════════════════
     Sidebar primary (left nav · árbol de docs)
     Densidad compacta — el árbol tiene 350+ entradas. Con
     line-height suelto + font 14px era inescaneable.
     ═══════════════════════════════════════════ */
  .md-sidebar--primary {
    width: 13.5rem !important;
  }
  .md-sidebar--primary .md-nav__link {
    font-size: 12.5px !important;
    line-height: 1.35 !important;
    padding: 0.28em 0.4em !important;
  }
  /* Sección (carpeta) headers: peso semibold + leve separación */
  .md-sidebar--primary .md-nav__item--section > .md-nav__link,
  .md-sidebar--primary .md-nav__item--nested > .md-nav__link {
    font-weight: 600 !important;
  }
  .md-sidebar--primary .md-nav__item--section {
    margin-top: 0.35em;
  }
  /* Niveles anidados: indent suave (0.6rem por nivel en lugar de
     el default que escala mucho con 4-5 niveles) */
  .md-sidebar--primary .md-nav .md-nav .md-nav__link {
    padding-left: 0.7rem !important;
  }
  .md-sidebar--primary .md-nav .md-nav .md-nav .md-nav__link {
    padding-left: 1.2rem !important;
  }
  .md-sidebar--primary .md-nav .md-nav .md-nav .md-nav .md-nav__link {
    padding-left: 1.7rem !important;
  }

  /* Título "AUNOA PLATFORM DOCUMENTATION" del sidebar */
  .md-nav__title {
    font-size: 10.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.06em !important;
    text-transform: uppercase !important;
    opacity: 0.65 !important;
    padding: 0.6em 0.6em 0.3em !important;
  }

  /* Item activo destacado con fondo suave + border-left corporativo
     (purple Aunoa). Reemplaza la barra azul agresiva del default. */
  .md-nav__link--active,
  .md-nav__item--active > .md-nav__link {
    color: var(--md-primary-fg-color) !important;
    font-weight: 600 !important;
    background: rgba(118, 113, 249, 0.10) !important;
    border-radius: 4px !important;
    border-left: 3px solid #7671F9 !important;
    padding-left: calc(0.4em - 3px) !important;
  }
  /* Hover sutil: indica que es interactivo sin agredir */
  .md-sidebar--primary .md-nav__link:hover {
    background: rgba(118, 113, 249, 0.05);
    color: var(--md-primary-fg-color);
  }
  .md-sidebar--primary .md-nav__link:hover:not(.md-nav__link--active) {
    transition: background 0.12s ease;
  }

  /* ═══════════════════════════════════════════
     Sidebar secundario (TOC derecha)
     ═══════════════════════════════════════════ */
  .md-sidebar--secondary {
    width: 11rem !important;
  }
  .md-sidebar--secondary .md-nav__link {
    font-size: 12px !important;
    line-height: 1.3 !important;
    padding: 0.2em 0 !important;
  }
  /* TOC: si la página tiene 0-1 H2/H3, el sidebar derecho es
     ruido. Lo escondemos con :empty trick — el resto JS lo refuerza
     según count de items útiles (ver expandActiveBranch). */
  .md-sidebar--secondary [data-aunoa-hide="true"] {
    display: none !important;
  }

  /* ═══════════════════════════════════════════
     Contenido (lo que ya teníamos, mantenido)
     ═══════════════════════════════════════════ */
  .md-typeset {
    font-size: 0.86rem !important;
    line-height: 1.65 !important;
  }
  .md-typeset h1 {
    margin-bottom: 0.5em !important;
    margin-top: 0 !important;
    font-size: 1.65rem !important;
  }
  .md-typeset h2 {
    margin-top: 1.4em !important;
    padding-bottom: 0.25em !important;
    border-bottom: 1px solid var(--md-default-fg-color--lightest) !important;
    font-size: 1.25rem !important;
  }
  .md-typeset table:not([class]) th,
  .md-typeset table:not([class]) td {
    padding: 0.55em 0.85em !important;
    font-size: 0.83rem !important;
  }
  .md-typeset code {
    font-size: 0.88em !important;
  }
  .md-typeset pre code {
    font-size: 0.81em !important;
  }

  /* Contenedor: menos padding top. Por defecto NO tocamos max-width —
     mkdocs material usa flex + fixed sidebars, y reventar el max-width
     hace que el contenido se solape con el TOC fixed a la derecha. */
  .md-content {
    padding-top: 6px !important;
  }
  .md-main__inner {
    margin-top: 0.5rem !important;
  }
  /* Cuando hideEmptyToc esconde el sidebar derecho, marca el grid con
     data-aunoa-no-toc; en ese caso SÍ podemos extender el contenido
     porque no hay TOC fixed contra el que solaparse. */
  .md-main__inner[data-aunoa-no-toc="true"] .md-content {
    max-width: none !important;
  }

  /* Footer: discreto */
  .md-footer {
    opacity: 0.75 !important;
  }

  /* Preserve the native drawer toggle: below 76.25em it is the only
     way to open the documentation tree. Backstage handles its visibility. */

  /* Breadcrumb interno de mkdocs (navigation.path): redundante en
     Backstage porque ya hay breadcrumb superior */
  .md-path {
    display: none !important;
  }
`;

/**
 * Marca como `checked` los toggles de los items activos para que el
 * sidebar muestre la ruta del documento abierto. Sin esto el sidebar
 * queda colapsado (los 3 items activos están marcados con la clase
 * `--active` pero sus checkboxes de expand están unchecked porque
 * mkdocs material asume que `navigation.expand` los habría dejado
 * abiertos — Backstage no ejecuta ese JS).
 */
function expandActiveBranch(sr: ShadowRoot) {
  const actives = sr.querySelectorAll(
    '.md-nav__item--active.md-nav__item--nested',
  );
  actives.forEach(item => {
    const toggle = item.querySelector(
      ':scope > input.md-nav__toggle',
    ) as HTMLInputElement | null;
    if (toggle && !toggle.checked) toggle.checked = true;
  });
}

/**
 * Esconde el sidebar derecho (TOC) cuando la página tiene <=2 items
 * útiles de primer nivel. Una columna entera para "Stack tecnologico
 * · Entornos" no justifica los 11rem de espacio horizontal: el lector
 * ve los headings directamente en el texto.
 *
 * Threshold: 2 (no 1) porque la mayoría de páginas index tienen
 * exactamente 2 secciones — y son el caso que más sufre con TOC vacío.
 * A partir de 3 items, el TOC empieza a ser útil para saltar.
 *
 * Cuando ocultamos el TOC, marcamos el grid con `data-aunoa-no-toc`
 * para que el CSS pueda expandir el contenido a ese espacio sin
 * solaparse (el TOC es position:fixed, así que sin ese hint el content
 * mantiene su max-width default y deja un hueco vacío a la derecha).
 */
function hideEmptyToc(sr: ShadowRoot) {
  const secondary = sr.querySelector('.md-sidebar--secondary');
  if (!secondary) return;
  const tocLinks = secondary.querySelectorAll(
    '.md-nav--secondary > .md-nav__list > .md-nav__item',
  );
  const el = secondary as HTMLElement;
  const grid = sr.querySelector('.md-main__inner');
  const hide = tocLinks.length <= 2;
  if (hide) {
    el.style.display = 'none';
    grid?.setAttribute('data-aunoa-no-toc', 'true');
  } else {
    if (el.style.display === 'none') el.style.display = '';
    grid?.removeAttribute('data-aunoa-no-toc');
  }
}

/**
 * TechDocs addon: inyecta CSS + JS dentro del shadow root del reader.
 * Solo Material UI envuelve TechDocs en sus jss-* classes; dentro del
 * shadow root sigue siendo mkdocs material con clases `.md-*`. Ese es
 * el handle por el que customizamos densidad y comportamiento.
 *
 * Importante: `useShadowRoot()` devuelve el ShadowRoot antes de que
 * Backstage haya inyectado el HTML estático del mkdocs build dentro.
 * Por eso el CSS lo añadimos eager (al primer tick), pero la lógica
 * que depende del DOM (expand active branch, hide empty TOC) la
 * armamos con MutationObserver y la disparamos cuando aparecen los
 * nodos relevantes. Sin esto, el sidebar queda colapsado al cargar.
 */
export const TechDocsCustomStyles = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return;

    // CSS: idempotente, válido incluso si el shadow está vacío
    if (!shadowRoot.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = customCss;
      shadowRoot.appendChild(style);
    }

    // Marcamos el body para que los overrides del chrome (fuera del
    // shadow root) se activen solo en techdocs. Backstage no añade una
    // clase intrínseca para distinguir páginas — esto sirve de hook.
    document.body.classList.add('aunoa-techdocs-active');

    // Inyectar el CSS del chrome en document.head (no en el shadow:
    // el header y la toolbar están fuera del shadow root).
    if (!document.getElementById(CHROME_STYLE_ID)) {
      const chromeStyle = document.createElement('style');
      chromeStyle.id = CHROME_STYLE_ID;
      chromeStyle.textContent = chromeCss;
      document.head.appendChild(chromeStyle);
    }

    const applyAll = () => {
      const hasNav =
        shadowRoot.querySelector('.md-nav__item--active.md-nav__item--nested') ||
        shadowRoot.querySelector('.md-sidebar--primary .md-nav');
      if (!hasNav) return;
      expandActiveBranch(shadowRoot);
      hideEmptyToc(shadowRoot);
    };

    // Intento inmediato (por si el HTML ya está ahí)
    applyAll();

    // Observa cambios en el shadow para detectar la inyección del
    // HTML de mkdocs y los cambios de página.
    const observer = new MutationObserver(() => {
      // Cheap re-check; applyAll es idempotente
      applyAll();
    });
    observer.observe(shadowRoot, { childList: true, subtree: true });

    // Re-aplicar al cambiar de URL (Backstage history)
    const onPop = () => applyAll();
    window.addEventListener('popstate', onPop);

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', onPop);
      shadowRoot.getElementById(STYLE_ID)?.remove();
      document.getElementById(CHROME_STYLE_ID)?.remove();
      document.body.classList.remove('aunoa-techdocs-active');
    };
  }, [shadowRoot]);

  return null;
};

// Register as a TechDocs Content addon (virtual — no visible UI)
attachTechDocsAddonComponentData(TechDocsCustomStyles, {
  name: 'TechDocsCustomStyles',
  location: TechDocsAddonLocations.Content,
  component: TechDocsCustomStyles,
});
